import { useEffect, useState } from "react";
import { Check, X, Edit3, Users, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface ProposedTask {
  title: string;
  details: string;
  lane: 'Bring' | 'Purchase' | 'To-Do';
  job_id: string | null;
  assignee_id: string | null;
  due_date: string | null;
  qty: number | null;
  units: string | null;
}

interface MeetingTaskConfirmationProps {
  summary: string;
  proposedTasks: ProposedTask[];
  jobs: Array<{ id: string; name: string }>;
  people: Array<{ id: string; name: string }>;
  onConfirm: (selectedTasks: ProposedTask[]) => void;
  onCancel: () => void;
  embedded?: boolean;
}

export const MeetingTaskConfirmation = ({
  summary,
  proposedTasks,
  jobs,
  people,
  onConfirm,
  onCancel,
  embedded = false,
}: MeetingTaskConfirmationProps) => {
  console.log('MeetingTaskConfirmation rendering with:', { summary, proposedTasks, jobs, people });
  
  const safeJobs = jobs || [];
  const safePeople = people || [];
  
  const [tasks, setTasks] = useState<Array<ProposedTask & { selected: boolean; editing: boolean }>>(
    (proposedTasks || []).map(task => ({ ...task, selected: true, editing: false }))
  );
  const [showConfirmReject, setShowConfirmReject] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  
  // Safety check
  if (!summary || !proposedTasks) {
    console.error('Missing required props:', { summary, proposedTasks });
    return null;
  }

  const selectedCount = tasks.filter(t => t.selected).length;
  const allSelected = selectedCount === tasks.length;

  const toggleAll = () => {
    const newState = !allSelected;
    setTasks(tasks.map(t => ({ ...t, selected: newState })));
  };

  const toggleTask = (index: number) => {
    setTasks(tasks.map((t, i) => i === index ? { ...t, selected: !t.selected } : t));
  };

  const toggleEdit = (index: number) => {
    setTasks(tasks.map((t, i) => i === index ? { ...t, editing: !t.editing } : t));
  };

  const updateTask = (index: number, updates: Partial<ProposedTask>) => {
    setTasks(tasks.map((t, i) => i === index ? { ...t, ...updates } : t));
  };

  const deleteTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    const selectedTasks = tasks.filter(t => t.selected).map(({ selected, editing, ...task }) => task);
    onConfirm(selectedTasks);
  };

  const handleReject = () => {
    if (showConfirmReject) {
      onCancel();
    } else {
      setShowConfirmReject(true);
    }
  };

  const getLaneBadgeColor = (lane: string) => {
    switch (lane) {
      case "Purchase": return "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20";
      case "Bring": return "bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20";
      case "To-Do": return "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20";
      default: return "";
    }
  };

  const Outer = ({ children }: { children: React.ReactNode }) => (
    embedded ? (
      <>{children}</>
    ) : (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">{children}</div>
    )
  );

  return (
    <Outer>
      <Card id="meeting-confirmation" className="w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl border-2">
        <CardHeader className="bg-primary text-primary-foreground sticky top-0 z-10">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Meeting Tasks Confirmation
          </CardTitle>
          <CardDescription className="text-primary-foreground/80">
            Review and select which tasks to create
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Summary */}
          <div className="bg-muted/50 p-4 rounded-lg border">
            <Label className="text-xs text-muted-foreground">MEETING SUMMARY</Label>
            <p className="text-sm mt-2">{summary}</p>
          </div>

          {/* Select All */}
          <div className="flex items-center justify-between pb-2 border-b">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                id="select-all"
              />
              <Label htmlFor="select-all" className="cursor-pointer">
                {selectedCount} of {tasks.length} tasks selected
              </Label>
            </div>
          </div>

          {/* Tasks List */}
          <div className="space-y-4">
            {tasks.map((task, index) => (
              <Card key={index} className={cn(
                "border-2 transition-all",
                task.selected ? "border-primary" : "border-muted opacity-60"
              )}>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={task.selected}
                      onCheckedChange={() => toggleTask(index)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getLaneBadgeColor(task.lane)}>
                            {task.lane}
                          </Badge>
                          {!task.editing && (
                            <h4 className="font-semibold">{task.title}</h4>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => toggleEdit(index)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteTask(index)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      {/* Editable Fields */}
                      {task.editing ? (
                        <div className="space-y-3 pl-2 border-l-2 border-primary">
                          <div>
                            <Label className="text-xs">Title</Label>
                            <Input
                              value={task.title}
                              onChange={(e) => updateTask(index, { title: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Details</Label>
                            <Textarea
                              value={task.details}
                              onChange={(e) => updateTask(index, { details: e.target.value })}
                              rows={2}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Job</Label>
                              <Select
                                value={task.job_id || ""}
                                onValueChange={(value) => updateTask(index, { job_id: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select job..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {safeJobs.map((job) => (
                                    <SelectItem key={job.id} value={job.id}>
                                      {job.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Assigned To</Label>
                              <Select
                                value={task.assignee_id || ""}
                                onValueChange={(value) => updateTask(index, { assignee_id: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Assign to..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {safePeople.map((person) => (
                                    <SelectItem key={person.id} value={person.id}>
                                      {person.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {(task.lane === "Purchase" || task.lane === "Bring") && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Quantity</Label>
                                <Input
                                  type="number"
                                  value={task.qty || ""}
                                  onChange={(e) => updateTask(index, { qty: parseFloat(e.target.value) || null })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Units</Label>
                                <Input
                                  value={task.units || ""}
                                  onChange={(e) => updateTask(index, { units: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm space-y-1 text-muted-foreground">
                          <p>{task.details}</p>
                          {task.job_id && (
                            <p className="text-xs">
                              Job: {safeJobs.find(j => j.id === task.job_id)?.name || "Unknown"}
                            </p>
                          )}
                          {task.assignee_id && (
                            <p className="text-xs">
                              Assigned: {safePeople.find(p => p.id === task.assignee_id)?.name || "Unknown"}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-background pb-2">
            {!showConfirmReject ? (
              <>
                <Button
                  onClick={handleConfirm}
                  disabled={selectedCount === 0}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Create {selectedCount} {selectedCount === 1 ? 'Task' : 'Tasks'}
                </Button>
                <Button
                  onClick={handleReject}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject All
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setShowConfirmReject(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  variant="destructive"
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Confirm Rejection
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </Outer>
  );
};
